/**
 * Custom Device Fingerprint Utility
 *
 * Tarayıcıdan toplanabilen sinyalleri birleştirip SHA256 hash üretir.
 * Bu hash bir cihazın yarı-kalıcı kimliğidir (MAC adresi gibi davranır).
 *
 * Toplanan sinyaller:
 * - User-Agent + Platform + Dil + Timezone
 * - Ekran çözünürlüğü + Renk derinliği + Pixel ratio
 * - Canvas render hash (GPU-bağımlı, neredeyse unique)
 * - WebGL vendor + renderer
 * - Audio context sample rate
 * - CPU çekirdek sayısı + Touch points
 *
 * Bağımlılık yok, GDPR/KVKK uyumlu (sadece tarayıcı içi hesap, sunucuya gönderilmez)
 */

/**
 * Web Crypto API ile SHA256 hash üret (async, modern tarayıcılar destekler)
 */
async function sha256(text) {
  const buf = new TextEncoder().encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buf);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Canvas render hash — GPU/font/anti-aliasing'e bağlı unique signature
 */
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';

    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('YETSAN-FP-2026', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('YETSAN-FP-2026', 4, 17);

    return canvas.toDataURL().slice(-100);  // son 100 char yeterli ayırt edici
  } catch (e) {
    return 'canvas-error';
  }
}

/**
 * WebGL vendor + renderer (GPU bilgisi)
 */
function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return 'no-webgl';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return 'no-debug-webgl';
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    return `${vendor}~${renderer}`;
  } catch (e) {
    return 'webgl-error';
  }
}

/**
 * Audio context sample rate (hardware-bağımlı)
 */
function getAudioFingerprint() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return 'no-audio';
    const ctx = new AudioCtx();
    const rate = ctx.sampleRate;
    ctx.close && ctx.close();
    return `sr-${rate}`;
  } catch (e) {
    return 'audio-error';
  }
}

/**
 * Ana fingerprint generator
 * Cihaz kimliği KALICI olarak hem localStorage HEM cookie'de saklanır.
 * Cookie, sürüm güncellemesindeki localStorage.clear()'dan etkilenmez (uzun ömürlü) →
 * localStorage silinse bile cihaz kimliği cookie'den geri yüklenir, cihaz hep tanınır.
 * Bu sayede "her güncellemeden sonra yeni cihaz/OTP" sorunu kalıcı çözülür.
 */
let _cached = null;
const FP_KEY = 'yetsan_device_fp_v1';

function _fpCookieOku() {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + FP_KEY + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) { return null; }
}

function _fpCookieYaz(deger) {
  try {
    const d = new Date();
    d.setTime(d.getTime() + 730 * 24 * 60 * 60 * 1000); // 2 yıl
    document.cookie = `${FP_KEY}=${encodeURIComponent(deger)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
  } catch (e) { /* ignore */ }
}

function _fpKaydet(hash) {
  try { localStorage.setItem(FP_KEY, hash); } catch (e) { /* ignore */ }
  _fpCookieYaz(hash);
}

export async function getDeviceFingerprint() {
  if (_cached) return _cached;

  // 1) localStorage
  try {
    const stored = localStorage.getItem(FP_KEY);
    if (stored && stored.length >= 32) {
      _cached = stored;
      _fpCookieYaz(stored); // cookie senkron tut
      return stored;
    }
  } catch (e) { /* ignore */ }

  // 2) cookie (localStorage silinmiş olabilir — sürüm güncellemesi/clear). Cihaz kimliğini geri yükle.
  const ck = _fpCookieOku();
  if (ck && ck.length >= 32) {
    _cached = ck;
    try { localStorage.setItem(FP_KEY, ck); } catch (e) { /* ignore */ }
    return ck;
  }

  // 3) Hiç yok → hesapla
  const signals = [
    navigator.userAgent || '',
    navigator.platform || '',
    navigator.language || '',
    (navigator.languages || []).join(','),
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    new Date().getTimezoneOffset(),
    `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
    window.devicePixelRatio || 1,
    navigator.hardwareConcurrency || 0,
    navigator.maxTouchPoints || 0,
    navigator.deviceMemory || 0,
    (navigator.cookieEnabled ? 1 : 0),
    getCanvasFingerprint(),
    getWebGLFingerprint(),
    getAudioFingerprint(),
  ];

  const raw = signals.join('||');
  const hash = await sha256(raw);
  _cached = hash;
  _fpKaydet(hash); // localStorage + cookie (kalıcı)
  return hash;
}

/**
 * Cihaz türü tespiti (mobil/web)
 */
export function getDeviceType() {
  const ua = navigator.userAgent.toLowerCase();
  const isMobil = /android|iphone|ipad|ipod|opera mini|iemobile|blackberry|webos|windows phone/i.test(ua);
  return isMobil ? 'mobil' : 'web';
}

/**
 * 🆕 Faz 2 — Backend'in cihaz kaydında saklayacağı zenginleştirme payload'ı.
 * (Hash'i etkilemez, sadece UI/admin tarafında okunabilir bilgi olarak saklanır.)
 */
export function getDeviceFingerprintExtras() {
  try {
    return {
      screen: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}x`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      cores: navigator.hardwareConcurrency || 0,
      platform: navigator.platform || '',
      lang: navigator.language || '',
    };
  } catch (e) {
    return {};
  }
}

/**
 * Cihaz için varsayılan label önerisi
 */
export function suggestDeviceLabel() {
  const ua = navigator.userAgent;
  const dt = getDeviceType();
  let os = 'Bilinmeyen';
  if (/windows/i.test(ua)) os = 'Windows';
  else if (/mac/i.test(ua)) os = 'macOS';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/iphone|ipad/i.test(ua)) os = 'iOS';
  else if (/linux/i.test(ua)) os = 'Linux';

  let browser = '';
  if (/edg/i.test(ua)) browser = 'Edge';
  else if (/chrome/i.test(ua)) browser = 'Chrome';
  else if (/safari/i.test(ua)) browser = 'Safari';
  else if (/firefox/i.test(ua)) browser = 'Firefox';

  return dt === 'mobil' ? `${os} ${browser} (Mobil)` : `${os} ${browser}`;
}

export default getDeviceFingerprint;
